//Common
import { DataGrid } from "../../common/VFX-UI";
import PageHeader from "../../common/PageHeader/PageHeader";
import ExportButton from "../../common/Buttons/ExportButton";
import SearchContainer from "../../common/Forms/SearchContainer/SearchContainer";

//App
import { FormikTypeaheadOption } from "../../../types";
import { SearchProps } from "../../../types";
import { SearchDefaultValue } from "../../../store/types";
import {
  EMPTY_GUID,
  SearchFieldsDefaultValue,
} from "../../../utility/constants/constants";
import { CampaignDesignationsReportProps } from "../../../types";
import useCampaignDesignationsReport from "./hooks/useCampaignDesignationReport";
import reportApi from "../../../app/model/api/reportApi";
import useCampaignStyles from "../../Campaign/styles";
import { CampaignDesignationReportDto } from "../../../app/model/api/types/dto";
import { useEffect, useRef, useState } from "react";

const CampaignDesignationsReport: React.FC<CampaignDesignationsReportProps> = (
  props
) => {
  const { data: campaignDesignations } = props;

  const { formContext, rowEvents } = useCampaignDesignationsReport({});
  const campaignStyles = useCampaignStyles();
    const [showSearchFields, setShowSearchFields] = useState<boolean>(false);
  const [filterContainerWidth, setFilterContainerWidth] = useState(0);
  const filterContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const filterContainer = filterContainerRef.current;
    if (!filterContainer) return;

    const resizeObserver = new ResizeObserver(([entry]) => {
      setFilterContainerWidth(entry.contentRect.width);
    });

    resizeObserver.observe(filterContainer);
    return () => resizeObserver.disconnect();
  }, []);
  const campaignDesignationRows = (campaignDesignations ?? []).map(
    (designation, index) => ({
      ...designation,
      rowId: `${designation.pledgeId ?? "no-pledge"}-${designation.agencyID ?? "no-agency"}-${designation.otherAgencyName ?? ""}-${index}`,
    })
  );

    const handleOnCampaignChange = async (value: FormikTypeaheadOption[]) => {
        let campaignId: string | null = null;
        let organizationId: string | null = null;

        if (value && value?.length > 0) {
            campaignId = (value[0] as Record<string, string | number>)[
                "value"
            ] as string;
            organizationId = (value[0] as Record<string, string | number>)[
                "organizationId"
            ] as string;
            setShowSearchFields(true);
        } else {
            setShowSearchFields(false);
        }
        await formContext?.setOrganizationId(organizationId);
        await formContext?.setId(campaignId);
    };

    const searchProps: SearchProps<CampaignDesignationReportDto> = {
        dropdown: {
            options: formContext?.campaigns ?? [],
            defaultSelected: formContext?.id != null ? [formContext.id] : [],
            clearButton: true,
            name: "campaignId",
            label: "Campaign",
            onChange: (value: any) => handleOnCampaignChange(value),
        },
        searchValue: formContext?.searchValue ?? SearchDefaultValue,
        searchFields: {
            ...SearchFieldsDefaultValue,
            name: showSearchFields,
            personnelNumber: showSearchFields,
            contributorType: showSearchFields,
            agency: showSearchFields,
            responded: showSearchFields,
            hasContributed: showSearchFields,
            methodOfGiving: showSearchFields,
        },
        setSearchValue: formContext?.setSearchValue,
        refetchGrid: undefined,
        contributorTypes: formContext?.contributorTypes,
        agencies: formContext?.agencies,
        filterLayout: "campaign-designations",
        breakActionsToNewRow: filterContainerWidth > 0 && filterContainerWidth < 1500,
        methodOfGivingTypes: formContext?.methodOfGivingTypes,
    };

  const columns = [
    {
      dataField: "personnelNumber",
      text: "Personnel No.",
      sort: true,
    },
    {
      dataField: "name",
      text: "Name",
      sort: true,
    },
    {
      dataField: "contributorType",
      text: "Contributor Type",
      sort: true,
    },
    {
      dataField: "responded",
      text: "Responded",
      sort: true,
      formatter: (cell: boolean | null) => (cell ? "Yes" : "No"),
    },
    {
      dataField: "divisionName",
      text: "Division Name",
      sort: false,
    },
    {
      dataField: "campaignName",
      text: "Campaign",
      sort: false,
    },
    {
      dataField: "campaignCode",
      text: "Campaign Code",
      sort: false,
    },
    {
      dataField: "methodOfGiving",
      text: "Method Of Giving",
      sort: false,
    },
      {
          dataField: "agency",
          text: "Agency",
          formatter: (cell: Date, row: CampaignDesignationReportDto) => {
              const hasOtherAgencyName =
                  row.otherAgencyName != null && row.otherAgencyName.length > 0;
              const otherAgencyNameSuffix = hasOtherAgencyName
                  ? ` - ${row.otherAgencyName}`
                  : "";

              return `${cell}${otherAgencyNameSuffix}`;
          },
          sort: false,
      },

      {
          dataField: "pledgeAmount",
          text: "Pledge Amount",
          sort: false,
          formatter: (cell: any) => {
              return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', }).format(cell);
          },
      },
    {
      dataField: "totalContribution",
      text: "Total Contribution",
      sort: false,
      formatter: (cell: any) => {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', }).format(cell);
      },
    },
  ];

  const exportUrl = reportApi.getCampaignDesignationsReportExport({
      ...formContext?.searchValue,
      campaignId: formContext?.id ?? EMPTY_GUID,
  });
  return (
      <>
          <PageHeader>Campaign Designations</PageHeader>
          <div style={campaignStyles.searchCampaignContainer}>
              <div ref={filterContainerRef} style={{ flex: 1, minWidth: 0 }}>
                  <SearchContainer searchProps={searchProps} />
              </div>
              {(campaignDesignations?.length !== 0) && campaignDesignations &&
                  <div className="mb-3" style={campaignStyles.completedButtonContainer}>
                      <ExportButton method="GET" url={exportUrl} fileName="Campaign Designations"/>
                  </div>
              }
          </div>
          <DataGrid
              columns={columns}
              data={campaignDesignationRows}
              keyField="rowId"
              rowEvents={rowEvents}
              totalSize={formContext?.paging?.totalSize}
              defaultSorted={[{
                  dataField: 'personnelNumber',
                  order: 'asc'
              }]}
              remote={true}
              pageSize={formContext?.pageSize}
              page={formContext?.paging?.currentPage}
              handlePageChange={async (newPage, pageSize) => {
                  if (formContext?.setCurrentPage && newPage) {
                      await formContext?.setCurrentPage(newPage);
                  }
                  if (formContext?.setCurrentPageSize && pageSize) {
                      await formContext?.setCurrentPageSize(pageSize);
                  }
              }}
          />
    </>
  );
};

export default CampaignDesignationsReport;
